/**
 * Task 299 — the complete non-operational agency experience.
 *
 * This page intentionally renders without effective permissions. Commercial
 * standing comes only from the dedicated Task 298 boundary, while the profile
 * editor uses Task 299's direct agency-role boundary. Neither is a permission
 * exemption and neither can unlock the product.
 */

import { Link } from 'react-router-dom';
import type { CommercialState } from '../api/types';
import { useMeContext } from '../context/MeContextProvider';
import { AgencyOnboardingPage } from './AgencyOnboardingPage';

interface StandingCopy {
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
}

/**
 * Task 300A — this map states the outcome SET, not an outcome: the
 * platform may decline, so no string may presuppose activation.
 * `ActivationPendingPage.test.ts` additionally forbids SaaS
 * commercial vocabulary («اشتراك», «باقة», «الدفع», «سعر») here.
 */
export const ACTIVATION_COPY: Readonly<Record<CommercialState, StandingCopy>> = {
  pending_activation: {
    eyebrow: 'بانتظار المراجعة',
    title: 'أكمل ملف الوكالة ثم انتظر قرار المراجعة',
    description:
      'حساب الوكالة مسجّل، لكنه لا يستطيع تشغيل العملاء أو المحتوى قبل أن يراجع فريق المنصة الطلب ويبتّ في تفعيله يدوياً.',
  },
  suspended: {
    eyebrow: 'الحساب موقوف',
    title: 'تم إيقاف حساب الوكالة مؤقتاً',
    description:
      'أسطح التشغيل وملف الوكالة غير متاحة في هذه الحالة. تواصل مع فريق المنصة لمعرفة التفاصيل.',
  },
  terminated: {
    eyebrow: 'الحساب منتهي',
    title: 'تم إنهاء حساب الوكالة',
    description:
      'لم يعد حساب الوكالة قادراً على استخدام النظام. تواصل مع فريق المنصة إذا كنت تعتقد أن هذا حدث عن طريق الخطأ.',
  },
  activated: {
    eyebrow: 'الحساب مفعّل',
    title: 'تم تفعيل حساب الوكالة',
    description: 'نعيد تحميل صلاحيات الحساب الآن لفتح مساحة العمل.',
  },
};

export interface ActivationPendingPageProps {
  readonly commercialState?: CommercialState;
  readonly clientUnavailable?: boolean;
  readonly refreshing?: boolean;
  readonly onRefresh?: () => void;
}

export function ActivationPendingPage({
  commercialState = 'pending_activation',
  clientUnavailable = false,
  refreshing = false,
  onRefresh,
}: ActivationPendingPageProps) {
  const { me, signOut } = useMeContext();
  const copy = ACTIVATION_COPY[commercialState];

  return (
    <main
      dir="rtl"
      style={{ minHeight: '100vh', background: 'var(--cream)', padding: '28px 22px 56px' }}
      data-testid="activation-pending-page"
      data-commercial-state={clientUnavailable ? 'unavailable' : commercialState}
    >
      <div style={{ maxWidth: 1040, margin: '0 auto' }}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 28,
          }}
        >
          <strong className="h-section">Super agency</strong>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link className="btn btn-ghost btn-sm" to="/support">
              المساعدة
            </Link>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              data-testid="activation-sign-out"
              onClick={() => void signOut()}
            >
              تسجيل الخروج
            </button>
          </div>
        </header>

        <section className="card" style={{ padding: 24, marginBottom: 20 }}>
          {clientUnavailable ? (
            <div data-testid="client-commercial-unavailable">
              <span className="pill">الوصول غير متاح</span>
              <h1 className="h-page" style={{ marginTop: 12 }}>
                مساحة العميل غير متاحة حالياً
              </h1>
              <p className="sub" style={{ marginTop: 8 }}>
                تواصل مع الوكالة التي تدير حسابك لمعرفة موعد عودة الوصول.
              </p>
            </div>
          ) : (
            <div>
              <span className="pill">{copy.eyebrow}</span>
              <h1 className="h-page" style={{ marginTop: 12 }}>
                {copy.title}
              </h1>
              <p className="sub" style={{ marginTop: 8, maxWidth: 760 }}>
                {copy.description}
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 18,
                  flexWrap: 'wrap',
                }}
              >
                {me?.profile?.email ? (
                  <span className="tiny" dir="ltr" data-testid="activation-account-email">
                    {me.profile.email}
                  </span>
                ) : (
                  <span />
                )}
                {onRefresh ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={onRefresh}
                    disabled={refreshing}
                    data-testid="activation-refresh"
                  >
                    {refreshing ? 'جاري التحقق…' : 'تحقق من حالة التفعيل'}
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </section>

        {!clientUnavailable && commercialState === 'pending_activation' ? (
          <>
            <section
              aria-label="خطوات التفعيل"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
                gap: 10,
                marginBottom: 20,
              }}
            >
              <ActivationStep number="1" title="تأكيد البريد" done />
              <ActivationStep number="2" title="إكمال ملف الوكالة" />
              {/* Task 300A — step 3 names the platform's step, not its
                  outcome; a numbered tracker otherwise asserts its last
                  step will be reached. */}
              <ActivationStep number="3" title="مراجعة المنصة وقرارها" />
            </section>
            <AgencyOnboardingPage />
          </>
        ) : null}
      </div>
    </main>
  );
}

function ActivationStep({
  number,
  title,
  done = false,
}: {
  readonly number: string;
  readonly title: string;
  readonly done?: boolean;
}) {
  return (
    <div className="card" style={{ padding: 15, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span className={done ? 'pill pill-success' : 'pill'}>{done ? 'تم' : number}</span>
      <strong>{title}</strong>
    </div>
  );
}
